import { useState, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { pendingInvite } from '../../components/School/inviteToken';
import { FiMail, FiLock, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import './Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  /* Hide the shared Layout header/footer so the auth screen gets the same
     self-contained, branded shell as the new logged-out homepage. */
  useLayoutEffect(() => {
    document.body.classList.add('auth-active');
    return () => document.body.classList.remove('auth-active');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Came from a school invite: straight back to it (it joins automatically)
      const invite = pendingInvite();
      navigate(invite ? `/join-school/${invite}` : '/dashboard');
    } catch (err) {
      setError('Failed to log in. Please check your credentials.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Log In - TeachESLPro | Access Your Teaching Resources</title>
        <meta name="description" content="Log in to your TeachESLPro account to access ESL teaching resources, download materials, and manage your custom lesson requests." />
      </Helmet>


      <main className="auth-main">
        <div className="auth-shell">
          <div className="auth-head">
            <h1>Welcome <em>back</em></h1>
            <p>Log in to access your teaching resources</p>
          </div>

          <div className="auth-card">
            {error && (
              <div className="auth-error">
                <FiAlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-field">
                <label htmlFor="email" className="auth-label">Email address</label>
                <div className="auth-input-wrap">
                  <span className="auth-input-icon"><FiMail /></span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-input"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="auth-field">
                <label htmlFor="password" className="auth-label">Password</label>
                <div className="auth-input-wrap">
                  <span className="auth-input-icon"><FiLock /></span>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="auth-input has-toggle"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="auth-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <Link to="/forgot-password" className="auth-forgot">
                Forgot your password?
              </Link>

              <button
                type="submit"
                disabled={loading}
                className="auth-btn auth-btn-primary auth-submit"
              >
                {loading ? 'Logging in...' : 'Log in'}
              </button>
            </form>

            <p className="auth-alt">
              Don&apos;t have an account?{' '}
              <Link to="/signup">Sign up</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
